import { action, makeObservable, observable } from 'mobx';

import type { IApiClient } from '../../../app/data/client';
import { HttpAbortedError } from '../../../app/data/HttpAbortedError';
import Logger from '../../../app/shared/Logger';
import { ModalState } from '../../../app/shared/ModalState';
import type { DownloadedFile, PricingContext } from '../../model';
import { GetCustomerPricelistExcelQuery } from '../../queries';

export type ExportPricelistToExcelStatus = 'idle' | 'pending' | 'success' | 'error';

/**
 * The file is made on the server each time the dealer asks for it, so it carries the prices as they are now
 * and the whole collection, regardless of the search term on the page behind it.
 */
export default class ExportPricelistToExcelModalState extends ModalState {
  @observable public status: ExportPricelistToExcelStatus = 'idle';
  /** Set once the server has made the file; saving it to the user's disk is left to the modal. */
  @observable.ref public file: DownloadedFile | null = null;
  @observable public includeNetPrice = false;

  /**
   * Aborts the request in flight once the modal is closed or the choice changes, so the server stops building
   * a file nobody is waiting for and a stale response never replaces the current one.
   */
  private abortController: AbortController | null = null;

  public constructor(
    private readonly client: IApiClient,
    private readonly pricingContext: PricingContext,
    private readonly pricelistId: string,
  ) {
    super();
    makeObservable(this);
  }

  public get canIncludeNetPrice(): boolean {
    return this.pricingContext.hasAgreements;
  }

  @action.bound
  public setIncludeNetPrice(includeNetPrice: boolean): void {
    this.includeNetPrice = includeNetPrice;

    this.abortRequest();
    this.setFile(null, 'idle');
  }

  @action.bound
  public createFile(): void {
    this.loadFile();
  }

  protected onAfterClose(): void {
    this.abortRequest();
    // Drops the file, so it is not kept in memory until the modal is opened again.
    this.setFile(null, 'idle');
    this.includeNetPrice = false;
    super.onAfterClose();
  }

  private async loadFile(): Promise<void> {
    this.abortRequest();

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.setFile(null, 'pending');

    try {
      const file = await this.client.send(
        new GetCustomerPricelistExcelQuery({
          pricingContext: this.pricingContext,
          pricelistId: this.pricelistId,
          includeNetPrice: this.canIncludeNetPrice && this.includeNetPrice,
        }),
        { signal },
      );

      this.setFile(file, 'success');
    } catch (error) {
      // Aborted because the modal was closed or the choice changed: nobody is waiting for this file any more.
      if (error instanceof HttpAbortedError) {
        return;
      }

      Logger.exception('Exporting the price list to Excel failed', error);
      this.setFile(null, 'error');
    }
  }

  private abortRequest(): void {
    this.abortController?.abort();
    this.abortController = null;
  }

  @action
  private setFile(file: DownloadedFile | null, status: ExportPricelistToExcelStatus): void {
    this.file = file;
    this.status = status;
  }
}
